import React, { useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';

const Section = ({ title, children }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div
      ref={ref}
      className="mb-10"
      initial={false}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
    >
      <h2 className="text-xl font-bold mb-4" style={{ color: '#2c3e50' }}>{title}</h2>
      {children}
    </motion.div>
  );
};

const P = ({ children }) => (
  <p className="text-sm leading-relaxed mb-3" style={{ color: '#6c757d' }}>{children}</p>
);

const TermsOfService = () => {
  const heroRef = useRef(null);
  const isHeroInView = useInView(heroRef, { once: true });

  return (
    <>
      <Helmet>
        <title>Terms of Service | Nexgeius - Toronto Software Development</title>
        <meta name="description" content="Read Nexgeius's terms of service outlining the conditions for using our website and engaging our custom software, website, and AI development services." />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href="https://www.nexgeius.com/terms" />
      </Helmet>

      {/* Hero */}
      <section
        className="relative overflow-hidden pt-32 pb-16 px-6 lg:px-8"
        style={{ backgroundColor: '#f7f9fc' }}
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle, rgba(44,62,80,0.07) 1px, transparent 1px)',
            backgroundSize: '28px 28px',
          }}
        />
        <div className="relative z-10 max-w-3xl mx-auto" ref={heroRef}>
          <motion.div
            initial={false}
            animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: '#6c757d' }}>Legal</p>
            <h1 className="text-4xl font-bold" style={{ color: '#2c3e50' }}>Terms of Service</h1>
            <p className="mt-4 text-sm" style={{ color: '#6c757d' }}>
              Last updated: January 1, 2025 &nbsp;·&nbsp; Nexgeius, Toronto, Ontario, Canada
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="pb-24 px-6 lg:px-8" style={{ backgroundColor: '#fff' }}>
        <div className="max-w-3xl mx-auto pt-14">

          <Section title="1. Agreement to Terms">
            <P>These Terms of Service ("Terms") govern your use of the Nexgeius website at nexgeius.com and any services provided by Nexgeius ("Company," "we," "us," or "our"). By accessing our website or engaging our services, you agree to be bound by these Terms.</P>
            <P>If you do not agree to these Terms, please do not use our website or services. We reserve the right to update these Terms at any time, and your continued use following any changes constitutes acceptance of the new Terms.</P>
          </Section>

          <Section title="2. Services">
            <P>Nexgeius provides website development, custom software, AI automation, and ongoing maintenance and support services (collectively, "Services").</P>
            <P>The specific scope, timeline, deliverables, and pricing for any engagement are governed by a separate written agreement or proposal agreed upon between Nexgeius and the client. These Terms apply in addition to any such agreement.</P>
          </Section>

          <Section title="3. Client Responsibilities">
            <P>To enable Nexgeius to deliver services effectively, clients agree to:</P>
            <ul className="text-sm leading-relaxed mb-3 pl-5 space-y-2" style={{ color: '#6c757d', listStyleType: 'disc' }}>
              <li>Provide accurate and complete project requirements and information</li>
              <li>Designate a primary point of contact authorized to make decisions</li>
              <li>Review and provide feedback on deliverables within agreed timeframes</li>
              <li>Pay invoices according to the payment terms outlined in the project agreement</li>
              <li>Provide lawful access to systems, credentials, and materials necessary to complete the work</li>
            </ul>
            <P>Delays caused by the client's failure to meet these responsibilities may result in revised timelines or additional fees, which will be communicated in advance.</P>
          </Section>

          <Section title="4. Intellectual Property">
            <P><strong style={{ color: '#2c3e50' }}>Client ownership:</strong> Upon receipt of full payment, the client owns all custom code, designs, and deliverables created specifically for their project, unless otherwise agreed in writing.</P>
            <P><strong style={{ color: '#2c3e50' }}>Third-party components:</strong> Deliverables may include open-source libraries, third-party APIs, or licensed assets. Ownership and licensing of these components is governed by their respective licenses, which we will document and communicate.</P>
            <P><strong style={{ color: '#2c3e50' }}>Nexgeius property:</strong> We retain ownership of all general frameworks, tools, templates, and methodologies used or developed by Nexgeius that are not custom-built specifically for your project.</P>
            <P><strong style={{ color: '#2c3e50' }}>Portfolio rights:</strong> Unless expressly requested otherwise in writing, Nexgeius reserves the right to reference and display the completed project in our portfolio, case studies, and marketing materials.</P>
          </Section>

          <Section title="5. Payment Terms">
            <P>Payment terms are established in the project agreement or proposal. Unless otherwise agreed:</P>
            <ul className="text-sm leading-relaxed mb-3 pl-5 space-y-2" style={{ color: '#6c757d', listStyleType: 'disc' }}>
              <li>A deposit (typically 25–50%) is required before work begins</li>
              <li>Remaining payments are tied to agreed project milestones or a set schedule</li>
              <li>Invoices are due within 14 days of issue unless otherwise specified</li>
              <li>Late payments may be subject to a 2% monthly interest charge</li>
            </ul>
            <P>All prices are in Canadian dollars (CAD) unless otherwise stated. Applicable taxes (HST) will be added to invoices where required by law.</P>
          </Section>

          <Section title="6. Confidentiality">
            <P>Both parties agree to keep confidential any proprietary information, business strategies, technical details, or client data shared during an engagement. This obligation survives the termination of any agreement.</P>
            <P>We will not disclose your confidential information to third parties except as required to deliver the services (e.g., to a subcontractor under equivalent confidentiality obligations) or as required by law.</P>
          </Section>

          <Section title="7. Warranties and Disclaimers">
            <P>Nexgeius warrants that our services will be performed in a professional and workmanlike manner consistent with industry standards. We will correct any material defects in our deliverables that are reported within 30 days of delivery.</P>
            <P>Our website and any free resources are provided "as is" without warranty of any kind, express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, or non-infringement.</P>
            <P>We do not warrant that our website will be uninterrupted, error-free, or free of viruses or other harmful components.</P>
          </Section>

          <Section title="8. Limitation of Liability">
            <P>To the maximum extent permitted by applicable law, Nexgeius shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, goodwill, or business opportunities, arising out of or related to our services or this agreement.</P>
            <P>Our total aggregate liability to any client for any claims arising out of an engagement shall not exceed the total fees paid by the client to Nexgeius in the three (3) months preceding the claim.</P>
          </Section>

          <Section title="9. Termination">
            <P>Either party may terminate a service engagement with 14 days' written notice. Upon termination, the client is responsible for payment of all work completed up to the termination date. Nexgeius will deliver all completed work upon receipt of payment for work performed.</P>
            <P>Nexgeius reserves the right to suspend or terminate services immediately in cases of non-payment, breach of these Terms, or conduct that is unlawful or harmful.</P>
          </Section>

          <Section title="10. Governing Law">
            <P>These Terms shall be governed by and construed in accordance with the laws of the Province of Ontario and the federal laws of Canada applicable therein, without regard to conflict of law principles.</P>
            <P>Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the courts located in Toronto, Ontario, Canada.</P>
          </Section>

          <Section title="11. Contact">
            <P>For questions about these Terms, please contact us:</P>
            <div
              className="rounded-xl p-5 mt-2"
              style={{ backgroundColor: '#f7f9fc', border: '1px solid rgba(44,62,80,0.08)' }}
            >
              <p className="text-sm font-semibold mb-1" style={{ color: '#2c3e50' }}>Nexgeius</p>
              <p className="text-sm" style={{ color: '#6c757d' }}>Toronto, Ontario, Canada</p>
              <p className="text-sm" style={{ color: '#6c757d' }}>nexgeius@gmail.com</p>
            </div>
          </Section>

          <div className="mt-10 pt-8" style={{ borderTop: '1px solid rgba(44,62,80,0.08)' }}>
            <Link to="/" className="text-sm font-semibold transition-colors hover:text-[#145374]" style={{ color: '#1c587a' }}>
              ← Back to Home
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default TermsOfService;
